import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth, useLang } from "../lib/context";
import { LayoutDashboard, FilePlus2, Search, ShieldCheck, Settings2, Activity, ScrollText, LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { useTheme, ThemeToggle } from "../lib/theme";

const NAV = (u) => {
  const p = new Set(u.permissions);
  const all = p.has("*");
  const items = [];
  if (!u.is_staff || u.role === "field_agent") {
    items.push(["/onboarding", "Home", FilePlus2], ["/merchant", "Dashboard", LayoutDashboard], ["/resume", "Applications", Search]);
  }
  if (all || p.has("sx.view")) items.push(["/dashboard", "SwitchX console", Activity]);
  if (all || p.has("app.view_all")) items.push(["/admin", "Review workspace", ShieldCheck]);
  if (all || p.has("integrations.view")) items.push(["/admin/integrations", "Integrations", Activity]);
  if (all || p.has("app.view_all")) items.push(["/admin/config", "Rules & templates", Settings2]);
  if (all || p.has("audit.view")) items.push(["/admin/audit", "Audit & users", ScrollText]);
  return items;
};

export const Logo = ({ dark = true, className = "" }) => (
  <span className={`inline-flex items-center gap-2 text-xl font-bold tracking-tight ${dark ? "text-white" : "text-[#07111F]"} ${className}`} data-testid="brand-logo">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#31D7FF]/60 bg-[#0B1730] text-sm font-extrabold text-[#31D7FF]">SX</span>Switch<span className="-ml-2 text-[#31D7FF]">X</span>
  </span>
);

export default function Shell({ children }) {
  const { user, logout, config } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [theme, toggle] = useTheme("app");
  const links = NAV(user);
  const Nav = () => (
    <nav className="flex flex-col gap-1 p-3">
      {links.map(([to, l, Icon]) => (
        <NavLink key={to} to={to} end onClick={() => setOpen(false)} data-testid={`nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `group flex items-center gap-3.5 rounded-xl px-4 py-3 text-sm transition-all duration-200 active:scale-[0.98] ${isActive ? "sx-grad font-semibold text-white shadow-lg shadow-fuchsia-600/25" : "font-medium text-slate-400 hover:bg-white/5 hover:text-white"}`}>
          <Icon className="h-[18px] w-[18px] transition-transform group-hover:scale-110" /> {t(l)}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="app-bg flex min-h-screen font-sans text-slate-900">
      <aside className={`${open ? "fixed inset-y-0 left-0 z-50 flex" : "hidden"} w-72 shrink-0 flex-col border-r border-white/5 bg-[#0E0B1F] text-slate-200 shadow-2xl md:sticky md:top-0 md:flex md:h-screen`}>
        <Link to="/app" className="flex h-20 flex-col justify-center border-b border-white/5 px-6">
          <Logo />
          <div className="mt-0.5 text-[11px] text-slate-400">Accounts · PG · QR · POS</div>
        </Link>
        <div className="px-6 pb-1 pt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Menu</div>
        <Nav />
        <div className="m-4 mt-auto flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#C6F432] text-sm font-bold text-[#0E0B1F]">{(user.name || user.email)[0].toUpperCase()}</span>
          <div className="min-w-0 text-xs">
            <div className="truncate font-semibold text-slate-100" data-testid="shell-user-email">{user.email}</div>
            <div className="text-slate-400">{user.role_label}</div>
          </div>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-20 items-center justify-between border-b border-slate-200/80 bg-white/75 px-4 backdrop-blur-xl sm:px-8">
          <button className="rounded-md p-2 md:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          {config?.is_demo && (
            <span data-testid="demo-env-banner" className="flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-800">
              <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" /> <span className="sm:hidden">Demo · sandbox</span><span className="hidden sm:inline">Demo environment · sandbox providers · no real bank actions</span>
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle theme={theme} toggle={toggle} className="border-slate-200 text-slate-600 hover:bg-slate-100" />
            <div className="flex rounded-full border border-slate-200 bg-slate-100 p-1 text-xs" role="group" aria-label="Language">
              {["en", "hi"].map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)} className={`rounded-full px-3 py-1.5 font-semibold transition-all ${lang === l ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{l === "en" ? "EN" : "हिं"}</button>
              ))}
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="flex min-h-[40px] items-center gap-2 rounded-full px-4 text-sm font-medium text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-600">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("Sign out")}</span>
            </button>
          </div>
        </header>
        <main className="fade-up mx-auto w-full max-w-7xl flex-1 space-y-6 p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}

export const PageHead = ({ title, sub, children, testid }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 data-testid={testid} className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
      {sub && <p className="mt-1.5 text-sm font-medium text-slate-500">{sub}</p>}
    </div>
    {children}
  </div>
);

export const Card = ({ children, className = "", ...p }) => <div className={`relative rounded-3xl border border-violet-100/80 bg-white/95 p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06),0_8px_24px_-12px_rgba(15,23,42,0.12)] backdrop-blur-md sm:p-6 ${className}`} {...p}>{children}</div>;

export const Btn = ({ variant = "primary", className = "", ...p }) => {
  const v = {
    primary: "sx-grad text-white shadow-md shadow-fuchsia-500/25 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-fuchsia-500/35",
    outline: "border border-slate-300 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50/60 hover:text-violet-700",
    ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
    danger: "bg-gradient-to-r from-rose-600 to-rose-500 text-white shadow-md shadow-rose-500/25 hover:-translate-y-0.5",
    accent: "bg-[#C6F432] text-[#0E0B1F] shadow-md shadow-lime-500/30 hover:-translate-y-0.5 hover:shadow-lg",
  }[variant];
  return <button className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${v} ${className}`} {...p} />;
};

export const Empty = ({ title, sub, children }) => (
  <div className="rounded-2xl border border-dashed border-indigo-200 bg-gradient-to-br from-white to-indigo-50/40 p-12 text-center">
    <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-100 to-cyan-100" />
    <div className="font-semibold text-slate-800">{title}</div>
    {sub && <div className="mt-1 text-sm text-slate-500">{sub}</div>}
    <div className="mt-4">{children}</div>
  </div>
);

export const Loading = () => (
  <div data-testid="loading" className="space-y-4 p-6">
    {[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-gradient-to-r from-slate-100 via-slate-200/70 to-slate-100" />)}
  </div>
);
